import React, { useState } from 'react';
import { Box, Button, CircularProgress, Grid, Link, TextField, Typography } from '@mui/material';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { emailValidationRegex } from '../../constants/regex';
import { useNetwork, ApiError } from '../../utils/network';
import { useShowSnackbar } from '../../utils/snackbar';
import PasswordField from '../../components/PasswordField';
import { CaptchaModal } from '../../components/CaptchaModal';
import { AuthShell, AuthCard } from '../../components/AuthShell';
import { PT } from '../../components/portalUi';
import { buildResetRequest, DETAIL_LIMITS, type ResetForm } from './buildResetRequest';

/* /account/2fa-reset — for the member who lost their authenticator AND their
 * recovery codes (server docs/2fa-recovery-and-reset.md, part 2).
 *
 * They give their email, their current password, and whatever they can tell us
 * about the account. The server's answer is the same words whether or not those
 * matched anything — the form must not be a way to learn who has an account or
 * who has 2FA on — so this page shows that answer and what happens next, and
 * never says "wrong password" or "no such account". If it was their account, a
 * request opens: 72 hours of waiting, an email with a one-click cancel, then an
 * admin decides; signing in with the authenticator or a recovery code in the
 * meantime cancels it. The captcha comes before the submit, like sign-up. */

type DetailKey = keyof typeof DETAIL_LIMITS;

const DETAILS: Array<{
  name: DetailKey;
  label: string;
  help?: string;
  multiline?: boolean;
  autoComplete: string;
  type?: string;
}> = [
  { name: 'fullName', label: 'Full name on the account', autoComplete: 'name' },
  { name: 'phone', label: 'Phone number on the account', autoComplete: 'tel', type: 'tel' },
  { name: 'country', label: 'Country', autoComplete: 'country-name' },
  { name: 'signupDate', label: 'When you signed up', help: 'Roughly — the month and year is enough.', autoComplete: 'off' },
  { name: 'discord', label: 'Discord name', help: 'If you gave one.', autoComplete: 'off' },
  {
    name: 'notes',
    label: 'Anything else that helps us confirm it is you',
    help: 'Packs you bought, cards you hold, a support ticket — never your password.',
    multiline: true,
    autoComplete: 'off'
  }
];

const SAME_ANSWER = "If those details match an account, we've emailed it with what happens next.";

const errorMessage = (e: ApiError | undefined, fallback: string) => e?.data?.message || fallback;

const retryWords = (e: ApiError | undefined) => {
  const secs = Number(e?.data?.retryAfter);
  if (Number.isFinite(secs) && secs > 0) {
    const mins = Math.max(1, Math.ceil(secs / 60));
    return `Too many requests from your connection. Please try again in ${mins} minute${mins === 1 ? '' : 's'}.`;
  }
  return 'Too many requests from your connection. Please try again in an hour.';
};

export const TwoFactorResetRequest = () => {
  const { post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [pending, setPending] = useState<ResetForm | null>(null);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<ResetForm>({
    defaultValues: {
      email: '',
      password: '',
      fullName: '',
      phone: '',
      country: '',
      signupDate: '',
      discord: '',
      notes: ''
    }
  });

  const onSubmit: SubmitHandler<ResetForm> = (data) => {
    if (sending) return;
    setPending(data);
    setCaptchaOpen(true);
  };

  /* Called by the captcha once solved. The modal closes first and `sending` holds
     off a second call: Turnstile re-fires its callback when a solved token expires,
     and one request is all a member gets three of an hour. */
  const send = async (captchaToken: string) => {
    if (!pending || sending) return;
    setSending(true);
    setCaptchaOpen(false);
    try {
      const res = await post('/auth/two-factor-reset', buildResetRequest(pending, captchaToken), false);
      setAnswer(res?.message || SAME_ANSWER);
      setPending(null);
    } catch (err) {
      const e = err as ApiError;
      showSnackbar(e?.status === 429 ? retryWords(e) : errorMessage(e, 'Something went wrong. Please try again.'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthShell maxWidth={520}>
      <AuthCard>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
          <Typography component="h1" variant="h5" sx={{ textAlign: 'center' }}>
            Reset two-factor authentication
          </Typography>

          {answer ? (
            <Box sx={{ mt: 3 }} role="status" tabIndex={-1}>
              <Typography sx={{ fontWeight: 700, color: PT.text, lineHeight: 1.5 }}>{answer}</Typography>
              <Box component="ul" sx={{ mt: 2, mb: 0, pl: 2.5, color: PT.textMuted, '& li': { mb: 1, lineHeight: 1.5 } }}>
                <li>Nothing on the account changes for 72 hours.</li>
                <li>
                  The email has a one-click cancel link. If you did not ask for this, use it — then reset your
                  password.
                </li>
                <li>
                  Find your authenticator or a recovery code in the meantime? Just sign in with it: that cancels
                  the request.
                </li>
                <li>
                  After the wait our team reviews what you told us. If the reset goes ahead you will be emailed,
                  and for 48 hours withdrawals, marketplace listings and purchases, wallet changes and
                  physical-card orders stay paused.
                </li>
              </Box>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Link component={ReactRouterLink} to="/login" variant="body2">
                  Back to sign in
                </Link>
              </Box>
            </Box>
          ) : (
            <>
              <Typography sx={{ mt: 1.5, color: PT.textMuted, lineHeight: 1.5, textAlign: 'center' }}>
                Lost your authenticator <em>and</em> your recovery codes? Tell us what you can about your account.
                Nothing changes for 72 hours, and the email we send has a cancel link that works until a decision
                is made.
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 13.5, color: PT.textMuted, lineHeight: 1.5, textAlign: 'center' }}>
                Still have a recovery code?{' '}
                <Link component={ReactRouterLink} to="/login">
                  Sign in with it instead
                </Link>{' '}
                — no waiting.
              </Typography>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: true,
                    // trimmed first: a phone keyboard adds a space after a suggestion
                    validate: (v) => emailValidationRegex.test(String(v).trim())
                  }}
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      inputRef={ref}
                      margin="normal"
                      required
                      fullWidth
                      label="Email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      error={Boolean(errors.email)}
                      helperText={errors.email && 'Please enter a valid email'}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, ...field } }) => (
                    <PasswordField
                      inputRef={ref}
                      margin="normal"
                      required
                      fullWidth
                      label="Current password"
                      autoComplete="current-password"
                      error={Boolean(errors.password)}
                      helperText={
                        errors.password ? 'Your password is required' : 'Your password still works; only the second step is lost.'
                      }
                      {...field}
                    />
                  )}
                />

                <Typography
                  sx={{
                    mt: 2.5,
                    mb: 0.5,
                    font: '800 11px/1 Montserrat, sans-serif',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: PT.teal
                  }}
                >
                  About your account
                </Typography>
                <Typography sx={{ fontSize: 13, color: PT.textMuted, mb: 0.5 }}>
                  All optional, but the more that matches what we hold, the easier it is to say yes.
                </Typography>
                {DETAILS.map((d) => (
                  <Controller
                    key={d.name}
                    name={d.name}
                    control={control}
                    render={({ field: { ref, ...field } }) => (
                      <TextField
                        inputRef={ref}
                        margin="dense"
                        fullWidth
                        size="small"
                        label={d.label}
                        helperText={d.help}
                        multiline={d.multiline}
                        minRows={d.multiline ? 3 : undefined}
                        type={d.type}
                        autoComplete={d.autoComplete}
                        inputProps={{ maxLength: DETAIL_LIMITS[d.name] }}
                        {...field}
                      />
                    )}
                  />
                ))}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={sending}
                  sx={{ mt: 3, mb: 2 }}
                  startIcon={sending ? <CircularProgress size={18} /> : undefined}
                >
                  {sending ? 'Sending…' : 'Ask for a reset'}
                </Button>
                <Grid container justifyContent="space-between">
                  <Grid item>
                    <Link component={ReactRouterLink} to="/login" variant="body2">
                      Back to sign in
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link component={ReactRouterLink} to="/set-password" variant="body2">
                      Forgot your password instead?
                    </Link>
                  </Grid>
                </Grid>
              </form>
            </>
          )}
        </Box>

        <CaptchaModal
          open={captchaOpen}
          onClose={() => {
            if (sending) return;
            setCaptchaOpen(false);
            setPending(null);
          }}
          onSuccess={send}
          title="Verify to continue"
          description="Complete the verification to send your request"
        />
      </AuthCard>
    </AuthShell>
  );
};
